"use client"

import React, { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { usePathname } from "next/navigation"
import { authAPI, configuratorTypesAPI } from "@/lib/api"

interface CompanyInfo {
  id: string
  name: string
  erpNumber?: string
}

export interface AuthUser {
  id: string
  name: string
  email: string
  position: string
  role: "user" | "admin"
  company?: CompanyInfo
  status?: string
}

interface ConfiguratorType {
  id: string
  name: string
  description?: string
  icon?: string
  is_active?: boolean
  user_enabled?: boolean
  admin_enabled?: boolean
  has_access?: boolean
  can_modify_settings?: boolean
  price_multipliers_active?: boolean
}

interface AuthContextValue {
  user: AuthUser | null
  configuratorTypes: ConfiguratorType[]
  loading: boolean
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const initialState: AuthContextValue = {
  user: null,
  configuratorTypes: [],
  loading: true,
  refresh: async () => {},
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const [state, setState] = useState<AuthContextValue>(initialState)

  const refresh = async () => {
    try {
      setState((s) => ({ ...s, loading: true }))
      const [profile, typesResponse] = await Promise.all([
        authAPI.getProfile(),
        configuratorTypesAPI.getConfiguratorTypes(),
      ])
      setState({
        user: profile,
        configuratorTypes: typesResponse.configuratorTypes,
        loading: false,
        refresh,
      })
    } catch (error) {
      // If fetching fails (e.g., 401), propagate by setting loading false but leaving data null/empty
      setState({ ...initialState, loading: false, refresh })
      // Optionally you could redirect to /login here
    }
  }

  useEffect(() => {
    const unauthenticatedPaths = ["/login", "/reset-password", "/register"]
    const isUnauthPath = unauthenticatedPaths.some(path => pathname.startsWith(path))

    if (isUnauthPath) {
      setState((s) => ({ ...s, loading: false }))
      return
    }

    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return <AuthContext.Provider value={{ ...state, refresh }}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return context
}
